import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '../components/ui/dropdown-menu';
import {
  Send, Trash2, MoreVertical, Ticket,
  MessageCircle, CheckCircle2, Copy,
  RefreshCw, Check, X as XIcon, Minus, CircleDot,
  Filter, Trophy
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function CouponList() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingId, setSendingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [sendResult, setSendResult] = useState(null);
  const [filterResult, setFilterResult] = useState('all');
  const [resultStats, setResultStats] = useState(null);

  useEffect(() => {
    fetchCoupons();
    fetchResultStats();
  }, []);

  const fetchCoupons = async () => {
    try {
      const res = await axios.get('/api/coupons');
      setCoupons(res.data);
    } catch (err) {
      toast.error('Failed to load coupons');
    } finally {
      setLoading(false);
    }
  };

  const fetchResultStats = async () => {
    try {
      const res = await axios.get('/api/coupons/stats/results');
      setResultStats(res.data);
    } catch (err) { /* ignore */ }
  };

  const sendToTelegram = async (couponId) => {
    setSendingId(couponId);
    try {
      const res = await axios.post(`/api/coupons/${couponId}/send/telegram`);
      setSendResult(res.data);
      toast.success(`Sent! Success: ${res.data.success}, Failed: ${res.data.failed}`);
      fetchCoupons();
    } catch (err) {
      toast.error('Send failed');
    } finally {
      setSendingId(null);
    }
  };

  const shareWhatsApp = async (couponId) => {
    try {
      const res = await axios.get(`/api/coupons/${couponId}/whatsapp`);
      window.open(res.data.url, '_blank');
    } catch (err) {
      toast.error('Failed to generate WhatsApp link');
    }
  };

  const copyWhatsAppLink = async (couponId) => {
    try {
      const res = await axios.get(`/api/coupons/${couponId}/whatsapp`);
      await navigator.clipboard.writeText(res.data.url);
      toast.success('WhatsApp link copied!');
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const updateResult = async (couponId, result) => {
    try {
      await axios.patch(`/api/coupons/${couponId}/result`, { result });
      toast.success(`Result updated: ${result.charAt(0).toUpperCase() + result.slice(1)}`);
      fetchCoupons();
      fetchResultStats();
    } catch (err) {
      toast.error('Failed to update result');
    }
  };

  const deleteCoupon = async () => {
    if (!deleteId) return;
    const idToDelete = deleteId;
    setDeleteId(null);
    try {
      await axios.delete(`/api/coupons/${idToDelete}`);
      toast.success('Coupon deleted');
      fetchCoupons();
      fetchResultStats();
    } catch (err) {
      toast.error('Failed to delete coupon');
    }
  };

  const getResultIcon = (result) => {
    switch (result) {
      case 'won': return <Check className="w-4 h-4 text-green-400" />;
      case 'lost': return <XIcon className="w-4 h-4 text-red-400" />;
      case 'void': return <Minus className="w-4 h-4 text-yellow-400" />;
      default: return <CircleDot className="w-4 h-4 text-blue-400" />;
    }
  };

  const getResultBadge = (result) => {
    switch (result) {
      case 'won': return 'bg-green-500/15 text-green-400 border border-green-500/25';
      case 'lost': return 'bg-red-500/15 text-red-400 border border-red-500/25';
      case 'void': return 'bg-yellow-500/15 text-yellow-400 border border-yellow-500/25';
      default: return 'bg-blue-500/15 text-blue-400 border border-blue-500/25';
    }
  };

  const getResultLabel = (result) => {
    switch (result) {
      case 'won': return 'Won';
      case 'lost': return 'Lost';
      case 'void': return 'Void';
      default: return 'Pending';
    }
  };

  const filteredCoupons = filterResult === 'all'
    ? coupons
    : coupons.filter(c => (c.result || 'pending') === filterResult);

  if (loading) {
    return (
      <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto space-y-4">
        <Skeleton className="h-8 w-48" />
        {[1,2,3].map(i => <Skeleton key={i} className="h-24" />)}
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto space-y-6 animate-fadeInUp">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-semibold" style={{ fontFamily: 'Space Grotesk' }}>
            Coupons
          </h1>
          <p className="text-sm text-muted-foreground">{filteredCoupons.length} coupon{filteredCoupons.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchCoupons} className="gap-2">
            <RefreshCw className="w-3 h-3" /> Refresh
          </Button>
        </div>
      </div>

      {/* Result Stats Bar */}
      {resultStats && (
        <div className="flex flex-wrap gap-2">
          {[
            { key: 'all', label: 'All', count: resultStats.total, color: 'bg-muted/30 text-foreground border-border/60' },
            { key: 'won', label: 'Won', count: resultStats.won, color: 'bg-green-500/10 text-green-400 border-green-500/20' },
            { key: 'lost', label: 'Lost', count: resultStats.lost, color: 'bg-red-500/10 text-red-400 border-red-500/20' },
            { key: 'pending', label: 'Pending', count: resultStats.pending, color: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilterResult(f.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all duration-150 ${
                filterResult === f.key
                  ? f.color + ' ring-1 ring-current/20'
                  : 'bg-muted/10 text-muted-foreground border-border/40 hover:bg-muted/20'
              }`}
            >
              {f.label} ({f.count})
            </button>
          ))}
          {resultStats.win_rate > 0 && (
            <div className="ml-auto flex items-center gap-1.5 text-xs text-primary font-semibold">
              <Trophy className="w-3.5 h-3.5" />
              {resultStats.win_rate}% Win Rate
            </div>
          )}
        </div>
      )}

      {/* Send Result Banner */}
      {sendResult && (
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-primary" />
              <div>
                <p className="text-sm font-medium">Send Complete</p>
                <p className="text-xs text-muted-foreground">
                  Total: {sendResult.total} | Success: {sendResult.success} | Failed: {sendResult.failed}
                </p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setSendResult(null)}>Close</Button>
          </CardContent>
        </Card>
      )}

      {/* Coupons List */}
      <div data-testid="coupons-table" className="space-y-3">
        {filteredCoupons.length === 0 ? (
          <Card className="bg-card/80 border-border/60">
            <CardContent className="py-12 text-center">
              <Ticket className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No coupons found</p>
            </CardContent>
          </Card>
        ) : (
          filteredCoupons.map((coupon) => (
            <Card key={coupon.id} className="bg-card/80 border-border/60 backdrop-blur coupon-row hover:border-border">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  {/* Result indicator */}
                  <div className="mt-1 flex-shrink-0">
                    {getResultIcon(coupon.result)}
                  </div>

                  {/* Image thumbnail */}
                  {coupon.image_url && (
                    <div className="hidden sm:block w-16 h-14 rounded-lg overflow-hidden flex-shrink-0 border border-border/40">
                      <img src={`${BACKEND_URL}${coupon.image_url}`} alt="Coupon" className="w-full h-full object-cover" />
                    </div>
                  )}

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="text-sm font-semibold truncate">
                        {coupon.home_team} vs {coupon.away_team}
                      </h3>
                      {coupon.score && (
                        <span className="text-sm font-mono font-bold tabular-nums px-1.5 py-0.5 rounded bg-muted/30">
                          {coupon.score}
                        </span>
                      )}
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold flex-shrink-0 ${getResultBadge(coupon.result)}`}>
                        {getResultLabel(coupon.result)}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0 ${
                        coupon.status === 'sent' ? 'badge-sent' : 'badge-draft'
                      }`}>
                        {coupon.status === 'sent' ? 'Sent' : 'Draft'}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                      <span>{coupon.league}</span>
                      <span>&bull;</span>
                      <span>{coupon.match_date}</span>
                      <span>&bull;</span>
                      <span className="font-mono tabular-nums text-primary">{coupon.odds}</span>
                      <span>&bull;</span>
                      <span className="font-medium text-foreground">{coupon.prediction}</span>
                    </div>
                    {coupon.description && (
                      <p className="text-xs text-muted-foreground mt-1 truncate">{coupon.description}</p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button
                      variant="outline" size="sm"
                      data-testid="coupon-send-telegram-button"
                      onClick={() => sendToTelegram(coupon.id)}
                      disabled={sendingId === coupon.id}
                      className="gap-1.5 text-xs hidden sm:flex"
                    >
                      {sendingId === coupon.id ? (
                        <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Send className="w-3 h-3" />
                      )}
                      Telegram
                    </Button>

                    <Button
                      variant="outline" size="sm"
                      data-testid="coupon-share-whatsapp-button"
                      onClick={() => shareWhatsApp(coupon.id)}
                      className="gap-1.5 text-xs hidden sm:flex"
                    >
                      <MessageCircle className="w-3 h-3" />
                      WhatsApp
                    </Button>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="coupon-row-actions-menu">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-52">
                        <DropdownMenuItem onClick={() => sendToTelegram(coupon.id)} className="sm:hidden">
                          <Send className="w-4 h-4 mr-2" /> Send to Telegram
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => shareWhatsApp(coupon.id)} className="sm:hidden">
                          <MessageCircle className="w-4 h-4 mr-2" /> Share on WhatsApp
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => copyWhatsAppLink(coupon.id)}>
                          <Copy className="w-4 h-4 mr-2" /> Copy WhatsApp Link
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => updateResult(coupon.id, 'won')}>
                          <Check className="w-4 h-4 mr-2 text-green-400" /> Mark as Won
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => updateResult(coupon.id, 'lost')}>
                          <XIcon className="w-4 h-4 mr-2 text-red-400" /> Mark as Lost
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => updateResult(coupon.id, 'void')}>
                          <Minus className="w-4 h-4 mr-2 text-yellow-400" /> Mark as Void
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => updateResult(coupon.id, 'pending')}>
                          <CircleDot className="w-4 h-4 mr-2 text-blue-400" /> Mark as Pending
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          data-testid="coupon-delete-button"
                          onClick={() => setDeleteId(coupon.id)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="w-4 h-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Coupon</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this coupon? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteCoupon} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
